/**
 * RefBlock.jsx — Bloc double « Référentiel officiel » ↔ « Enrichi Code 2 Roues ».
 *
 * Utilisé sur les pages du Permis AM Voiturette pour distinguer CLAIREMENT
 * le référentiel réglementaire (arrêté du 8 novembre 2012 modifié) du
 * contenu enrichi ajouté par Code 2 Roues (exercices, QCM, activités).
 *
 * Deux variantes :
 *   - `<RefOfficiel>` : bordure BLEUE + badge 📜 « Officiel — Arrêté 08/11/2012 »
 *   - `<RefEnrichi>`  : bordure ROUGE + badge 💡 « Enrichi Code 2 Roues »
 *
 * Chacun accepte un `sourceUrl` (par défaut Légifrance) qui rend l'attribution
 * cliquable pour tout élève / enseignant curieux de la source réglementaire.
 */
import React from 'react';
import { ScrollText, Lightbulb, ExternalLink } from 'lucide-react';

const LEGIFRANCE_URL =
  'https://www.legifrance.gouv.fr/loda/id/JORFTEXT000026611026/';

export function RefOfficiel({
  title,
  subtitle,
  duration,
  sourceUrl = LEGIFRANCE_URL,
  sourceLabel = 'Arrêté 08/11/2012 modifié · Annexe II',
  children,
  testid,
}) {
  return (
    <section
      className="rounded-2xl border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 via-sky-500/5 to-transparent overflow-hidden"
      data-testid={testid || 'ref-officiel'}
    >
      <header className="px-4 py-3 sm:px-5 border-b border-sky-500/25 bg-sky-500/10 flex flex-col sm:flex-row sm:items-start sm:gap-3 sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-black uppercase tracking-widest text-sky-300 mb-1">
            <span className="inline-flex items-center gap-1.5"><ScrollText className="w-3 h-3" /> Référentiel officiel</span>
            {duration && <span className="px-1.5 py-0.5 rounded bg-sky-500/20 border border-sky-500/40 text-[9.5px]">{duration}</span>}
          </div>
          {title && (
            <h3 className="text-white text-base sm:text-lg font-black leading-tight break-words" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {title}
            </h3>
          )}
          {subtitle && <p className="text-sky-100/85 text-[12.5px] mt-0.5 italic">{subtitle}</p>}
        </div>
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 sm:mt-0 self-start inline-flex items-center gap-1 h-7 px-2 rounded-md bg-white/10 hover:bg-white/15 border border-white/15 text-white/85 text-[10px] font-bold uppercase tracking-widest transition whitespace-nowrap max-w-full overflow-hidden text-ellipsis"
          data-testid="ref-officiel-source"
          aria-label={`Source : ${sourceLabel}`}
        >
          <span className="truncate">{sourceLabel}</span> <ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      </header>
      <div className="px-4 py-4 sm:px-5 sm:py-5 text-white/85 text-[13.5px] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export function RefEnrichi({ title, subtitle, children, testid }) {
  return (
    <section
      className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent overflow-hidden"
      data-testid={testid || 'ref-enrichi'}
    >
      <header className="px-4 py-3 sm:px-5 border-b border-red-500/25 bg-red-500/10">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">
          <Lightbulb className="w-3 h-3" /> Enrichi Code 2 Roues · pédagogie
        </div>
        {title && (
          <h3 className="text-white text-base sm:text-lg font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {title}
          </h3>
        )}
        {subtitle && <p className="text-red-100/85 text-[12.5px] mt-0.5 italic">{subtitle}</p>}
      </header>
      <div className="px-4 py-4 sm:px-5 sm:py-5 text-white/85 text-[13.5px] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

/**
 * Wrapper à 2 colonnes (Officiel · Enrichi) sur desktop, empilé sur mobile.
 * Usage :
 *   <RefPair>
 *     <RefOfficiel>...</RefOfficiel>
 *     <RefEnrichi>...</RefEnrichi>
 *   </RefPair>
 */
export function RefPair({ children, testid }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={testid || 'ref-pair'}>
      {children}
    </div>
  );
}

/**
 * Liste sémantique utilisée dans les blocs officiels — bullets propres,
 * même style que les listes du référentiel, mais dark-mode friendly.
 */
export function RefList({ items, tone = 'sky' }) {
  const toneClass = tone === 'red' ? 'text-red-300' : 'text-sky-300';
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className={`shrink-0 mt-2 w-1 h-1 rounded-full bg-current ${toneClass}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default RefOfficiel;
